// This service handles form submission to the email service API
// The email API will send form data via email

const EMAIL_API_URL = process.env.REACT_APP_EMAIL_API_URL || '';

const createEmailTemplate = (formData) => {
  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const htmlTemplate = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
          }
          .container {
            background: #f9f9f9;
            padding: 20px;
            border-radius: 8px;
            border: 1px solid #e0e0e0;
          }
          .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 30px;
            border-radius: 8px 8px 0 0;
            text-align: center;
            margin: -20px -20px 20px -20px;
          }
          .header h1 {
            margin: 0;
            font-size: 28px;
            font-weight: bold;
          }
          .header p {
            margin: 10px 0 0 0;
            font-size: 14px;
            opacity: 0.9;
          }
          .form-section {
            margin: 20px 0;
            padding: 15px;
            background: white;
            border-left: 4px solid #667eea;
            border-radius: 4px;
          }
          .form-section h3 {
            margin: 0 0 15px 0;
            color: #667eea;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 15px;
          }
          .form-row.full {
            grid-template-columns: 1fr;
          }
          .form-field {
            margin-bottom: 15px;
          }
          .form-field:last-child {
            margin-bottom: 0;
          }
          .form-field label {
            font-weight: 600;
            color: #555;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            display: block;
            margin-bottom: 6px;
          }
          .form-field value {
            font-size: 15px;
            color: #333;
            padding: 8px 0;
            word-break: break-word;
          }
          .timestamp {
            text-align: center;
            margin-top: 20px;
            padding-top: 15px;
            border-top: 1px solid #eee;
            font-size: 12px;
            color: #999;
          }
          .footer {
            text-align: center;
            margin-top: 20px;
            padding-top: 15px;
            border-top: 1px solid #eee;
            font-size: 13px;
            color: #666;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>🎯 New Waitlist Signup</h1>
            <p>The AI-First CHRO</p>
          </div>

          <div class="form-section">
            <h3>Personal Information</h3>
            <div class="form-row">
              <div class="form-field">
                <label>Full Name</label>
                <value>${formData.name || 'N/A'}</value>
              </div>
              <div class="form-field">
                <label>Work Email</label>
                <value>${formData.email || 'N/A'}</value>
              </div>
            </div>
            <div class="form-row full">
              <div class="form-field">
                <label>LinkedIn Profile</label>
                <value><a href="${formData.linkedin}" target="_blank">${formData.linkedin || 'N/A'}</a></value>
              </div>
            </div>
          </div>

          <div class="form-section">
            <h3>Company Information</h3>
            <div class="form-row">
              <div class="form-field">
                <label>Company</label>
                <value>${formData.company || 'N/A'}</value>
              </div>
              <div class="form-field">
                <label>Job Title</label>
                <value>${formData.title || 'N/A'}</value>
              </div>
            </div>
            <div class="form-row">
              <div class="form-field">
                <label>Employee Count</label>
                <value>${formData.size || 'N/A'}</value>
              </div>
              <div class="form-field">
                <label>Phone</label>
                <value>${formData.phone || 'N/A'}</value>
              </div>
            </div>
          </div>

          <div class="form-section">
            <h3>Contact Information</h3>
            <div class="form-row full">
              <div class="form-field">
                <label>Address</label>
                <value>${formData.address || 'N/A'}</value>
              </div>
            </div>
          </div>

          <div class="timestamp">
            <strong>Submitted on:</strong> ${formatDate(new Date().toISOString())}
          </div>

          <div class="footer">
            <p>This is an automated message from The AI-First CHRO waitlist system.</p>
          </div>
        </div>
      </body>
    </html>
  `;

  return htmlTemplate;
};

/* Email template for the Believer/Achiever self-assessment.
   Renders the result, every question with the chosen answer, and the optional
   demographics. Mirrors the styling of the waitlist template above. */
const createAssessmentEmailTemplate = (payload) => {
  const { result = {}, answers = {}, demographics = {}, meta = {} } = payload || {};

  const formatDate = (isoString) => {
    const d = isoString ? new Date(isoString) : new Date();
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const answerRows = Object.keys(answers)
    .map((key, i) => {
      const a = answers[key] || {};
      const question = a.question || `Question ${i + 1}`;
      const chosen = a.label != null ? a.label : 'Not answered';
      return `
        <div class="form-field">
          <label>Q${i + 1}. ${question}</label>
          <value>${chosen}</value>
        </div>`;
    })
    .join('');

  const val = (v) => (v != null && v !== '' ? v : 'Prefer not to say');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; }
          .container { background: #f9f9f9; padding: 20px; border-radius: 8px; border: 1px solid #e0e0e0; }
          .header { background: linear-gradient(135deg, #EE1B24 0%, #7a0d12 100%); color: white; padding: 30px; border-radius: 8px 8px 0 0; text-align: center; margin: -20px -20px 20px -20px; }
          .header h1 { margin: 0; font-size: 26px; font-weight: bold; }
          .header p { margin: 10px 0 0 0; font-size: 14px; opacity: 0.9; }
          .form-section { margin: 20px 0; padding: 15px; background: white; border-left: 4px solid #EE1B24; border-radius: 4px; }
          .form-section h3 { margin: 0 0 15px 0; color: #EE1B24; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; }
          .form-field { margin-bottom: 15px; }
          .form-field:last-child { margin-bottom: 0; }
          .form-field label { font-weight: 600; color: #555; font-size: 13px; display: block; margin-bottom: 6px; }
          .form-field value { font-size: 15px; color: #333; display: block; word-break: break-word; }
          .badge { display: inline-block; background: #EE1B24; color: #fff; padding: 4px 12px; border-radius: 999px; font-size: 13px; font-weight: 600; }
          .timestamp { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #eee; font-size: 12px; color: #999; }
          .footer { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #eee; font-size: 13px; color: #666; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>📊 New Assessment Result</h1>
            <p>Believer or Achiever · The AI-First CHRO</p>
          </div>

          <div class="form-section">
            <h3>Result</h3>
            <div class="form-field">
              <label>Group</label>
              <value><span class="badge">${val(result.group)}</span></value>
            </div>
            <div class="form-field">
              <label>Stage</label>
              <value>${val(result.stage)}</value>
            </div>
            <div class="form-field">
              <label>AI Tourism flag</label>
              <value>${result.aiTourist ? 'Yes' : 'No'}</value>
            </div>
          </div>

          <div class="form-section">
            <h3>Answers</h3>
            ${answerRows || '<div class="form-field"><value>No answers recorded</value></div>'}
          </div>

          <div class="form-section">
            <h3>About the function (optional)</h3>
            <div class="form-field">
              <label>Role</label>
              <value>${val(demographics.role)}</value>
            </div>
            <div class="form-field">
              <label>Organisation size</label>
              <value>${val(demographics.orgSize)}</value>
            </div>
            <div class="form-field">
              <label>Industry</label>
              <value>${val(demographics.industry)}</value>
            </div>
          </div>

          <div class="timestamp">
            <strong>Submitted on:</strong> ${formatDate(meta.ts)}
            ${meta.completedInSec ? ` · Completed in ${meta.completedInSec}s` : ''}
          </div>

          <div class="footer">
            <p>This is an automated message from The AI-First CHRO assessment.</p>
          </div>
        </div>
      </body>
    </html>
  `;
};

/* Sends the completed assessment (answers + optional demographics) by email,
   reusing the same email API as the waitlist form. Anonymous — no PII collected. */
export const submitAssessmentResult = async (payload) => {
  if (!EMAIL_API_URL) {
    console.error('Email API URL not configured. Missing REACT_APP_EMAIL_API_URL');
    throw new Error('Email service not configured. Missing REACT_APP_EMAIL_API_URL');
  }

  const emailPayload = {
    subject: '📊 New Assessment Result - Believer or Achiever',
    bcc: 'amit@iona.ai',
    email: 'amit@iona.ai',
    msg: createAssessmentEmailTemplate(payload)
  };

  const response = await fetch(EMAIL_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(emailPayload)
  });

  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.message || `Failed to send email. Status: ${response.status}`);
  }
  return { success: true };
};

export const submitFormToGoogleSheet = async (formData) => {
  if (!EMAIL_API_URL) {
    console.error('Email API URL not configured. Available env vars:', Object.keys(process.env));
    throw new Error('Email service not configured. Missing REACT_APP_EMAIL_API_URL');
  }

  try {
    const emailPayload = {
      subject: '🎯 New Waitlist Signup - The AI-First CHRO',
      bcc: 'sethiatul53@gmail.com', // Recipient email
        email:'amit@iona.ai',
      msg: createEmailTemplate(formData)
    };

    console.log('Sending email to:', EMAIL_API_URL);
    console.log('Payload:', emailPayload);

    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailPayload)
    });

    console.log('Response status:', response.status);
    const result = await response.json();
    console.log('Response body:', result);
    
    if (!response.ok) {
      throw new Error(result.message || `Failed to send email. Status: ${response.status}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Email submission error:', error);
    throw error;
  }
};
